import React, { useState, useMemo } from 'react';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import api from '../../../utils/api';
import { toast } from 'sonner';
import {
  Search, Loader2, Wallet, CalendarDays, TrendingDown, AlertTriangle,
  ArrowUpDown, ArrowUp, ArrowDown, Building2, Mail, Landmark, X
} from 'lucide-react';

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function OwnerReconstructionTab({ year }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', method: '', spese: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'paid_at', direction: 'asc' });

  const doSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/owner-reconstruction/search?q=${encodeURIComponent(q.trim())}`);
      setResults(res.data || []);
    } catch { toast.error('Errore nella ricerca'); }
    setSearching(false);
  };

  const loadReconstruction = async (prop, y = year) => {
    setSelected(prop);
    setResults([]);
    setLoading(true);
    try {
      const res = await api.get(`/owner-reconstruction/${prop.id}?year=${y}`);
      setData(res.data);
    } catch { toast.error('Errore nel caricamento della ricostruzione'); setData(null); }
    setLoading(false);
  };

  React.useEffect(() => {
    if (selected) loadReconstruction(selected, year);
  }, [year]);

  const payments = useMemo(() => {
    if (!data) return [];
    const q = filters.search.trim().toLowerCase();
    let list = data.payments.filter(p => {
      if (q && !(`${p.booking_code} ${p.guest_name || ''}`.toLowerCase().includes(q))) return false;
      if (filters.method && p.method !== filters.method) return false;
      if (filters.spese === 'con' && !(p.cost_amount > 0)) return false;
      if (filters.spese === 'senza' && p.cost_amount > 0) return false;
      return true;
    });
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const val = (p) => {
      switch (sortConfig.key) {
        case 'paid_at': return p.paid_at || '';
        case 'booking': return (p.booking_code || '').toLowerCase();
        case 'check_in': return p.check_in || '';
        case 'nights': return p.nights || 0;
        case 'rsrv': return p.rsrv_amount || 0;
        case 'cost': return p.cost_amount || 0;
        case 'total': return p.total || 0;
        case 'method': return p.method || '';
        case 'residuo': return p.residuo_after ?? 0;
        default: return '';
      }
    };
    list.sort((a, b) => { const va = val(a), vb = val(b); return va < vb ? -dir : va > vb ? dir : 0; });
    return list;
  }, [data, filters, sortConfig]);

  const handleSort = (key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const sortIcon = (key) => sortConfig.key !== key
    ? <ArrowUpDown className="h-3 w-3 text-slate-300" />
    : (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 text-blue-600" /> : <ArrowDown className="h-3 w-3 text-blue-600" />);
  const Th = ({ k, children, right }) => (
    <th className={`px-2.5 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:bg-slate-100 ${right ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(k)} data-testid={`ricostruzione-sort-${k}`}>
      <span className={`flex items-center gap-1 ${right ? 'justify-end' : ''}`}>{children} {sortIcon(k)}</span>
    </th>
  );

  const b = data?.budget;

  return (
    <div className="space-y-4" data-testid="ricostruzione-tab">
      {/* Ricerca proprietario */}
      <Card className="p-4">
        <label className="text-sm font-medium text-slate-700 mb-1 block">Cerca proprietario o immobile</label>
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={e => doSearch(e.target.value)}
            placeholder="Es: Cascavilla, Kala Flat, ISI31907..." className="pl-10" data-testid="ricostruzione-search" />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="ricostruzione-results">
              {results.map(r => (
                <button key={r.id} onClick={() => { setQuery(`${r.owner_name || ''} — ${r.name}`); loadReconstruction(r); }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0"
                  data-testid={`ricostruzione-result-${r.property_code}`}>
                  <span className="text-sm font-medium text-slate-800">{r.owner_name || '(proprietario n.d.)'}</span>
                  <span className="block text-xs text-slate-400">{r.property_code} · {r.name} · {r.city}{r.status === 'terminated' ? ' · CESSATO' : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {loading && <Card className="p-8 flex items-center justify-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Ricostruzione in corso...</Card>}

      {!loading && data && (
        <>
          {/* Header proprietario */}
          <Card className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <p className="text-lg font-bold text-slate-800" data-testid="ricostruzione-owner-name">{data.owner.name || 'Proprietario n.d.'}</p>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {data.property.code} · {data.property.name} · {data.property.city}</p>
            </div>
            {data.owner.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {data.owner.email}</p>}
            {data.owner.iban && <p className="text-xs text-slate-500 flex items-center gap-1 font-mono"><Landmark className="h-3.5 w-3.5" /> {data.owner.iban}</p>}
          </Card>

          {/* Riepilogo budget */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3">
              <p className="text-[11px] text-slate-400 uppercase flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Budget costi {data.year}</p>
              <p className="text-xl font-bold text-slate-800" data-testid="ricostruzione-budget">{fmtEur(b.effective)}</p>
              {b.carry_over_year === data.year && b.carry_over > 0 && <p className="text-[11px] text-slate-400">di cui carry-over {fmtEur(b.carry_over)}</p>}
            </Card>
            <Card className="p-3">
              <p className="text-[11px] text-slate-400 uppercase flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Incidenza giornaliera</p>
              <p className="text-xl font-bold text-slate-800">{fmtEur(b.incidence)}</p>
              {b.incidence > 0 && <p className="text-[11px] text-slate-400">≈ {Math.floor(b.effective / b.incidence)} giorni coperti</p>}
            </Card>
            <Card className="p-3">
              <p className="text-[11px] text-slate-400 uppercase flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> Consumato</p>
              <p className="text-xl font-bold text-slate-800" data-testid="ricostruzione-consumato">{fmtEur(b.consumed_total)}</p>
              <p className="text-[11px] text-slate-400">spese {fmtEur(b.cost_paid_total)} + chiusure {fmtEur(b.closures_amount)} ({b.closures_days} gg)</p>
            </Card>
            <Card className={`p-3 ${b.exhausted ? 'border-red-300 bg-red-50/60' : ''}`}>
              <p className="text-[11px] text-slate-400 uppercase">Residuo</p>
              <p className={`text-xl font-bold ${b.exhausted ? 'text-red-600' : 'text-green-600'}`} data-testid="ricostruzione-residuo">{fmtEur(b.residual)}</p>
              {b.exhausted && <p className="text-[11px] text-red-500 font-medium">Budget esaurito</p>}
            </Card>
          </div>

          {!b.configured && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="ricostruzione-alert-config">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Budget costi o incidenza giornaliera non configurati su questo immobile: in questo caso il sistema versa tutto come <strong>locazione</strong>.</span>
            </div>
          )}
          {b.exhausted && b.exhausted_at && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" data-testid="ricostruzione-alert-esaurito">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Il budget costi {data.year} risulta <strong>esaurito dal {fmtD(b.exhausted_at)}</strong>: da quella data i pagamenti sono partiti interamente come <strong>locazione</strong> (comportamento previsto dal cap annuale).</span>
            </div>
          )}

          {/* Filtri + totali */}
          <Card className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder="Cerca prenotazione/ospite..." className="pl-8 h-9 w-56" data-testid="ricostruzione-filter-search" />
              </div>
              <select value={filters.method} onChange={e => setFilters(f => ({ ...f, method: e.target.value }))}
                className="p-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="ricostruzione-filter-method">
                <option value="">Metodo: tutti</option>
                <option value="bonifico">Bonifico SEPA</option>
                <option value="paypal">PayPal</option>
              </select>
              <select value={filters.spese} onChange={e => setFilters(f => ({ ...f, spese: e.target.value }))}
                className="p-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="ricostruzione-filter-spese">
                <option value="">Spese: tutti</option>
                <option value="con">Solo con quota spese</option>
                <option value="senza">Solo senza quota spese (tutto locazione)</option>
              </select>
              {(filters.search || filters.method || filters.spese) && (
                <button onClick={() => setFilters({ search: '', method: '', spese: '' })}
                  className="text-xs text-blue-600 flex items-center gap-1" data-testid="ricostruzione-filter-reset"><X className="h-3.5 w-3.5" /> Azzera</button>
              )}
              <div className="ml-auto text-xs text-slate-500" data-testid="ricostruzione-totali">
                {payments.length} pagamenti · Locazione <strong>{fmtEur(data.totals.rsrv)}</strong> · Spese <strong>{fmtEur(data.totals.cost)}</strong> · Totale <strong>{fmtEur(data.totals.total)}</strong>
              </div>
            </div>
            {data.payments_without_date > 0 && (
              <p className="text-[11px] text-amber-600 mt-1">{data.payments_without_date} pagamenti con data mancante non mostrati</p>
            )}
          </Card>

          {/* Tabella pagamenti */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ricostruzione-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <Th k="paid_at">Pagato il</Th>
                    <Th k="booking">Prenotazione</Th>
                    <Th k="check_in">Soggiorno</Th>
                    <Th k="nights" right>Notti</Th>
                    <Th k="rsrv" right>Locazione</Th>
                    <Th k="cost" right>Spese</Th>
                    <Th k="total" right>Totale</Th>
                    <Th k="method">Metodo</Th>
                    <Th k="residuo" right>Residuo budget</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.length === 0 ? (
                    <tr><td colSpan="9" className="p-6 text-center text-slate-400">Nessun pagamento trovato per il {data.year}</td></tr>
                  ) : payments.map(p => (
                    <tr key={p.booking_id} className={p.cost_amount > 0 ? '' : 'bg-amber-50/40'} data-testid={`ricostruzione-row-${p.booking_code}`}>
                      <td className="px-2.5 py-2 whitespace-nowrap">{fmtD(p.paid_at)}</td>
                      <td className="px-2.5 py-2">
                        <span className="font-medium text-slate-800">{p.booking_code}</span>
                        {p.guest_name && <span className="block text-xs text-slate-400 truncate max-w-[140px]">{p.guest_name}</span>}
                      </td>
                      <td className="px-2.5 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtD(p.check_in)} → {fmtD(p.check_out)}</td>
                      <td className="px-2.5 py-2 text-right">{p.nights}</td>
                      <td className="px-2.5 py-2 text-right font-medium text-slate-700">{fmtEur(p.rsrv_amount)}</td>
                      <td className={`px-2.5 py-2 text-right font-medium ${p.cost_amount > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {fmtEur(p.cost_amount)}
                        {p.cap_reached && p.cost_teorico > p.cost_amount && (
                          <span className="block text-[10px] text-slate-400" title="Quota spese teorica (notti × incidenza)">teorico {fmtEur(p.cost_teorico)}</span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 text-right font-semibold">{fmtEur(p.total)}</td>
                      <td className="px-2.5 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.method === 'bonifico' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {p.method === 'bonifico' ? 'Bonifico' : 'PayPal'}
                        </span>
                        {!p.split_known && <span className="block text-[10px] text-slate-400 mt-0.5">split n.d.</span>}
                      </td>
                      <td className={`px-2.5 py-2 text-right text-xs ${p.residuo_after <= 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>{fmtEur(p.residuo_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 px-3 py-2 border-t border-slate-100">
              Le righe evidenziate in giallo sono pagamenti partiti interamente (o quasi) come locazione. "Residuo budget" = budget costi rimanente dopo quel pagamento (incluso il consumo delle chiusure proprietario fino a quella data).
            </p>
          </Card>
        </>
      )}

      {!loading && !data && !selected && (
        <Card className="p-8 text-center text-slate-400 text-sm">
          Cerca un proprietario (es. <button onClick={() => doSearch('Cascavilla')} className="text-blue-600 underline">Cascavilla</button>) per ricostruire tutti i pagamenti con split locazione/spese.
        </Card>
      )}
    </div>
  );
}
